import { gql } from '../typed-gql';
import {
    APPOINTMENT_FRAGMENT,
    APPOINTMENT_PREVIEW_FRAGMENT,
    CREDENTIAL_FRAGMENT,
    COUNTRY_FRAGMENT,
    DIAGNOSIS_CODE_FRAGMENT,
    DIAGNOSIS_FRAGMENT,
    DIAGNOSIS_WITH_APPT_FRAGMENT,
    DRUG_FRAGMENT,
    EMOTION_FRAGMENT,
    ETHNICITY_FRAGMENT,
    INSURANCE_CARRIER_FRAGMENT,
    LANGUAGE_FRAGMENT,
    MEDICAL_INTAKE_FRAGMENT,
    PARTNERSHIP_STATUS_FRAGMENT,
    PAYMENT_FRAGMENT,
    PRONOUN_FRAGMENT,
    PSYCH_CONDITION_FRAGMENT,
    SPECIALTY_FRAGMENT,
    STATE_FRAGMENT,
    TIMEZONE_FRAGMENT,
    USER_FRAGMENT,
    CLIENT_USER_FRAGMENT,
    PROVIDER_USER_FRAGMENT,
    USER_MINI_FRAGMENT,
    JOURNAL_FRAGMENT,
    CONVERSATION_PREVIEW_FRAGMENT,
    CONVERSATION_FRAGMENT,
    INSURANCE_CLAIM_FRAGMENT,
    CPT_CODE_FRAGMENT,
    CLIENT_PLAN_FRAGMENT,
    SESSION_DATA_FRAGMENT,
    RELEASE_FRAGMENT,
    DOCUMENT_FRAGMENT,
    PROVIDER_CREDENTIALING_DATA_FRAGMENT,
    ASSESSMENT_FRAGMENT,
    PAYOUT_FRAGMENT,
    OWED_PAYOUT_INFO_FRAGMENT,
    OWED_PAYOUT_INFO_BREAKDOWN_FRAGMENT,
    POLICY_FRAGMENT,
    ORGANIZATION_PREVIEW_FRAGMENT,
    ORGANIZATION_FRAGMENT,
    QUESTIONNAIRE_FRAGMENT,
    QUESTIONNAIRE_QUESTION_FRAGMENT,
    MESSAGE_FRAGMENT,
    MEDICAL_DOCUMENT_FRAGMENT,
    FAX_JOB_FRAGMENT,
    METRIC_FRAGMENT,
    DELTA_FRAGMENT,
    TOPIC_FRAGMENT,
    GROUP_CLIENT_PREVIEW_FRAGMENT,
    GROUP_CLIENT_PREVIEW_CONNECTION_FRAGMENT,
    GROUP_PREVIEW_FRAGMENT,
    GROUP_CONNECTION_FRAGMENT,
    GROUP_STATS_FRAGMENT as GROUPS_STATS_FRAGMENT,
    GROUP_SESSION_PREVIEW_CONNECTION_FRAGMENT,
    GROUP_SESSIONS_CONNECTION_FRAGMENT,
    GROUP_FRAGMENT,
    GROUPS_SUBSCRIPTION_FRAGMENT,
    REACHLINK_PLUS_SUBSCRIPTION_FRAGMENT,
    GROUP_PLATFORM_CONFIG_FRAGMENT,
    GROUP_MEMBER_CONNECTION_FRAGMENT,
    ATTENDANCE_RECORD_CONNECTION_FRAGMENT,
    GROUP_MEMBERSHIP_EVENT_CONNECTION_FRAGMENT,
    GROUP_SESSION_ANALYTICS_CONNECTION_FRAGMENT,
    CLIENT_GROUP_MEMBERSHIP_CONNECTION_FRAGMENT,
    CLIENT_OWN_GROUP_MEMBERSHIP_CONNECTION_FRAGMENT,
    GROUP_PROFIT_FRAGMENT,
    GROUP_SALES_SUMMARY_FRAGMENT,
    GROUP_SESSIONS_SUMMARY_FRAGMENT,
    GROUP_OVERALL_SUMMARY_FRAGMENT,
    GROUP_CONNECTION_WITH_ANALYTICS_FRAGMENT,
    GROUP_CONNECTION_WITH_SESSIONS_FRAGMENT,
    GROUP_SALES_TIME_SERIES_FRAGMENT,
    GROUP_SESSIONS_TIME_SERIES_FRAGMENT,
    BENEFIT_VERIFICATION_FRAGMENT,
    UPLOAD_FRAGMENT,
    ASSIGNMENT_SUBMISSION_FRAGMENT,
    PAGINATED_ASSIGNMENT_SUBMISSIONS_FRAGMENT,
    CLIENT_ASSIGNMENT_FRAGMENT,
    AVAILABLE_SUBSCRIPTION_PLAN_FRAGMENT,
    CLIENT_GROUP_MEMBERSHIP_DETAILS_FRAGMENT,
    MESSAGE_CONNECTION_FRAGMENT,
    MESSAGING_CONVERSATION_FRAGMENT,
    MESSAGING_CONVERSATION_PREVIEW_CONNECTION_FRAGMENT,
    MESSAGING_CONVERSATION_CONNECTION_FRAGMENT,
    MESSAGING_RECIPIENT_CONNECTION_FRAGMENT,
    ESCALATION_FRAGMENT,
    ESCALATION_PREVIEW_CONNECTION_FRAGMENT,
    ESCALATION_CONNECTION_FRAGMENT,
    REPORTED_MESSAGE_CONNECTION_FRAGMENT,
    REFERRAL_CODE_FRAGMENT,
    CC_REFERRAL_PROGRESS_FRAGMENT,
    CALL_CONNECT_FRAGMENT,
    GROUP_SESSION_PREVIEW_FRAGMENT,
    GROUP_PROMO_CODE_VALIDATION_FRAGMENT,
    GROUP_SESSION_FRAGMENT,
    GROUP_SESSION_RSVP_FRAGMENT,
    PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT,
    PROVIDER_FEEDBACK_LOG_DETAIL_FRAGMENT,
} from './fragments';

/*
 * Guest/Static
 */

export const GET_LANGUAGES = gql`
    ${LANGUAGE_FRAGMENT}
    query Guest {
        languages {
            ...languageFields
        }
    }
`;

export const GET_COUNTRIES = gql`
    ${COUNTRY_FRAGMENT}
    query Guest {
        countries {
            ...countryFields
        }
    }
`;

export const GET_STATES = gql`
    ${STATE_FRAGMENT}
    query Guest {
        states {
            ...stateFields
        }
    }
`;

export const GET_INSURANCE_CARRIERS = gql`
    ${INSURANCE_CARRIER_FRAGMENT}
    query Guest {
        insuranceCarriers {
            ...insuranceCarrierFields
        }
    }
`;

export const GET_AUTO_PANEL_SYNC_STATUSES = gql`
    query AutoPanelSyncStatuses {
        autoPanelSyncStatuses {
            insuranceCarrierId
            pendingCount
        }
    }
`;

export const GET_AUTO_PANEL_PREVIEW = gql`
    query AutoPanelPreview($insuranceCarrierId: ID!) {
        autoPanelPreview(insuranceCarrierId: $insuranceCarrierId) {
            pendingProviders {
                userId
                name
                status
            }
            autoPaneledProviders {
                userId
                name
                status
            }
        }
    }
`;

const ROSTER_PROVIDER_FIELDS = `
    userId
    name
    npi
    status
    isActive
`;

export const PREVIEW_CARRIER_ROSTER = gql`
    query PreviewCarrierRoster($insuranceCarrierId: ID!, $rows: [RosterRowInput!]!) {
        previewCarrierRoster(insuranceCarrierId: $insuranceCarrierId, rows: $rows) {
            rows {
                rowIndex
                name
                npi
                effectiveDate
                matchedBy
                provider { ${ROSTER_PROVIDER_FIELDS} }
                candidates { ${ROSTER_PROVIDER_FIELDS} }
            }
            unlistedPaneledProviders { ${ROSTER_PROVIDER_FIELDS} }
            providers { ${ROSTER_PROVIDER_FIELDS} }
        }
    }
`;

export const GET_CPT_CODES = gql`
    ${CPT_CODE_FRAGMENT}
    query Guest {
        cptCodes {
            ...cptCodeFields
        }
    }
`;

export const GET_TIMEZONES = gql`
    ${TIMEZONE_FRAGMENT}
    query Guest {
        timezones {
            ...timezoneFields
        }
    }
`;

export const GET_PRONOUNS = gql`
    ${PRONOUN_FRAGMENT}
    query Guest {
        pronouns {
            ...pronounFields
        }
    }
`;

export const GET_ETHNICITIES = gql`
    ${ETHNICITY_FRAGMENT}
    query Guest {
        ethnicities {
            ...ethnicityFields
        }
    }
`;

export const GET_SPECIALTIES = gql`
    ${SPECIALTY_FRAGMENT}
    query Guest {
        specialties {
            ...specialtyFields
        }
    }
`;

export const GET_CERTIFICATIONS = gql`
    ${CREDENTIAL_FRAGMENT}
    query Guest {
        credentials {
            ...credentialFields
        }
    }
`;

export const GET_PARTNERSHIP_STATUSES = gql`
    ${PARTNERSHIP_STATUS_FRAGMENT}
    query Guest {
        partnershipStatuses {
            ...partnershipStatusFields
        }
    }
`;

export const GET_DRUGS = gql`
    ${DRUG_FRAGMENT}
    query Guest {
        drugs {
            ...drugFields
        }
    }
`;

export const GET_PSYCH_CONDITIONS = gql`
    ${PSYCH_CONDITION_FRAGMENT}
    query Guest {
        psychConditions {
            ...psychConditionFields
        }
    }
`;

export const GET_EMOTIONS = gql`
    ${EMOTION_FRAGMENT}
    query Guest {
        emotions {
            ...emotionFields
        }
    }
`;

export const GET_DIAGNOSIS_CODES = gql`
    ${DIAGNOSIS_CODE_FRAGMENT}
    query Guest {
        diagnosisCodes {
            ...diagnosisCodeFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_QUESTIONNAIRES = gql`
    ${QUESTIONNAIRE_FRAGMENT}
    query Questionnaires {
        questionnaires {
            ...questionnaireFields
        }
    }
`;

/*
 * Auth/Tokens
 */

export const VERIFY_TOKEN = gql`
    query Guest($token: String!) {
        verifyToken(token: $token) {
            valid
            data
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_BETTERMODE_AUTH = gql`
    query GetBettermodeAuth {
        getBettermodeAuth {
            ssoUrl
        }
    }
`;

export const GET_INTERCOM_AUTH = gql`
    query GetIntercomAuth($platform: String) {
        getIntercomAuth(platform: $platform) {
            token
        }
    }
`;

export const GET_MINOR_CONSENT = gql`
    query Guest($token: String, $clientUserId: ID) {
        minorConsent(token: $token, clientUserId: $clientUserId) {
            minorFirstName
            minorLastName
            parentFirstName
            parentLastName
            parentEmail
            parentPhoneNumber
            parentDateOfBirth
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const CHECK_OAUTH_REDIRECT = gql`
    query CheckOAuthRedirect {
        checkOAuthRedirect
    }
`;

/*
 * Policies
 */

export const GET_POLICIES = gql`
    ${POLICY_FRAGMENT}
    query Policies($clientUserId: ID!, $policyType: PolicyType) {
        policies(clientUserId: $clientUserId, policyType: $policyType) {
            ...policyFields
        }
    }
`;

export const GET_BENEFIT_VERIFICATIONS = gql`
    ${BENEFIT_VERIFICATION_FRAGMENT}
    query BenefitVerifications($policyId: ID!) {
        benefitVerifications(policyId: $policyId) {
            ...benefitVerificationFields
        }
    }
`;

/*
 * Users
 */

export const GET_USER = gql`
    ${USER_FRAGMENT}
    query User($userId: ID) {
        user(userId: $userId) {
            ...userFields
        }
    }
`;

export const GET_CLIENT = gql`
    ${CLIENT_USER_FRAGMENT}
    query Client($clientUserId: ID!) {
        client(clientUserId: $clientUserId) {
            ...clientUserFields
        }
    }
`;

// Small lookup for the session-mode booking popover: the `client` objects
// available at the popover's call sites come from different queries, and
// most do not select sessionMode/isPhoneCallSupported.
export const GET_CLIENT_SESSION_MODE_INFO = gql`
    query ClientSessionModeInfo($clientUserId: ID!) {
        client(clientUserId: $clientUserId) {
            userId
            clientProfile {
                sessionMode
                isPhoneCallSupported
            }
        }
    }
`;

export const GET_CLIENT_SUSPENSION = gql`
    query ClientSuspension($clientUserId: ID!) {
        client(clientUserId: $clientUserId) {
            userId
            suspensionReason
            suspensionDetails {
                reason
                suspendedAt
                acknowledgedAt
                appointmentId
            }
        }
    }
`;

export const GET_SUSPENDED_SESSION = gql`
    query SuspendedSession($appointmentId: ID!) {
        appointment(appointmentId: $appointmentId) {
            appointmentId
            startDateTime
            scheduledTimezone
            provider {
                userId
                name
            }
        }
    }
`;

export const SEARCH_CLIENTS = gql`
    ${USER_MINI_FRAGMENT}
    query SearchClients(
        $searchQuery: ClientSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        clients(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            users {
                ...userMiniFields
                phoneNumber
                email
                gender
                createdAt
                isRegistrationComplete
                role {
                    name
                }
                country {
                    abbreviation
                }
                clientProfile {
                    coverageType
                    sessionType
                    status
                    lastFollowup
                    sendFollowups
                    state
                    State {
                        name
                        abbreviation
                    }
                    insuranceCarrier {
                        name
                    }
                }
            }
            total
        }
    }
`;

/** Admin clients export: the clients search, selecting only the exported columns. */
export const EXPORT_CLIENTS = gql`
    query ExportClients(
        $searchQuery: ClientSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        clients(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            users {
                userId
                name
                phoneNumber
                email
                country {
                    abbreviation
                }
                clientProfile {
                    state
                    coverageType
                    insuranceCarrier {
                        name
                    }
                    sessionType
                    lastFollowup
                    sendFollowups
                }
            }
            total
        }
    }
`;

export const SEARCH_PROVIDERS = gql`
    ${USER_MINI_FRAGMENT}
    query SearchProviders(
        $searchQuery: ProviderSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        providers(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            users {
                ...userMiniFields
                isActive
                createdAt
                country {
                    abbreviation
                }
                providerProfile {
                    states
                    acceptsNewClients
                    acceptsEapClients
                    bookingPriority
                    isApproved
                    recruitingStatus
                }
            }
            total
        }
    }
`;

/** Admin providers export: the providers search, selecting only the exported columns. */
export const EXPORT_PROVIDERS = gql`
    query ExportProviders(
        $searchQuery: ProviderSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        providers(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            users {
                userId
                name
                email
                phoneNumber
                isActive
                createdAt
                country {
                    abbreviation
                }
                providerProfile {
                    states
                    isApproved
                    acceptsNewClients
                    acceptsEapClients
                    bookingPriority
                    recruitingStatus
                }
            }
            total
        }
    }
`;

/**
 * Every provider for the network-partner client-mode booking search, with the
 * extra fields it filters by locally (contact, gender, ethnicity, specialties).
 * Run once per visit with limit -1; everything after is client-side.
 */
export const SEARCH_BOOKING_PROVIDERS = gql`
    ${USER_MINI_FRAGMENT}
    query SearchBookingProviders($searchQuery: ProviderSearchQuery!, $limit: Int!) {
        providers(searchQuery: $searchQuery, offset: 0, limit: $limit) {
            users {
                ...userMiniFields
                isActive
                createdAt
                email
                phoneNumber
                gender
                ethnicityId
                timezone
                providerProfile {
                    states
                    specialtyIds
                    carriers {
                        insuranceCarrierId
                        status
                    }
                    acceptsNewClients
                    acceptsEapClients
                    bookingPriority
                    isApproved
                }
            }
            total
        }
    }
`;

export const GET_AVAILABLE_PROVIDERS = gql`
    ${USER_MINI_FRAGMENT}
    ${CREDENTIAL_FRAGMENT}
    query AvailableProviders($clientUserId: ID!, $doVolumeCooldown: Boolean) {
        availableProviders(clientUserId: $clientUserId, doVolumeCooldown: $doVolumeCooldown) {
            provider {
                ...userMiniFields
                role {
                    name
                }
                providerProfile {
                    credential {
                        ...credentialFields
                    }
                    specialties {
                        specialtyId
                        name
                    }
                }
            }
            tags {
                label
                severity
                symbol
            }
            rating {
                average
                count
            }
        }
    }
`;

export const GET_PAGINATED_AVAILABLE_PROVIDERS_PREVIEW = gql`
    ${USER_MINI_FRAGMENT}
    ${CREDENTIAL_FRAGMENT}
    query PaginatedAvailableProviders(
        $clientUserId: ID!
        $doVolumeCooldown: Boolean
        $limit: Int
        $after: ProviderPaginationCursorInput
        $before: ProviderPaginationCursorInput
        $sortBy: ProviderSortBy
        $orderBy: SortOrder
        $filters: AvailableProviderFiltersInput
    ) {
        paginatedAvailableProviders(
            clientUserId: $clientUserId
            doVolumeCooldown: $doVolumeCooldown
            limit: $limit
            after: $after
            before: $before
            sortBy: $sortBy
            orderBy: $orderBy
            filters: $filters
        ) {
            items {
                provider {
                    ...userMiniFields
                    providerProfile {
                        credential {
                            ...credentialFields
                        }
                        specialties {
                            specialtyId
                            name
                        }
                    }
                }
                rating {
                    average
                    count
                }
                phoneCallSupported
            }
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor {
                    id
                    value
                }
                endCursor {
                    id
                    value
                }
            }
        }
    }
`;

export const GET_PROVIDER = gql`
    ${PROVIDER_USER_FRAGMENT}
    query Provider($providerUserId: ID!) {
        provider(providerUserId: $providerUserId) {
            ...providerUserFields
        }
    }
`;

export const GET_STRIPE_ONBOARDING_STATUS = gql`
    query GetStripeOnboardingStatus($providerUserId: ID!) {
        stripeOnboardingStatus(providerUserId: $providerUserId) {
            completed
        }
    }
`;

export const GET_PROVIDER_CREDENTIALING_DATA = gql`
    ${PROVIDER_CREDENTIALING_DATA_FRAGMENT}
    query GetProviderCredentialingData($providerUserId: ID!) {
        providerCredentialingData(providerUserId: $providerUserId) {
            ...providerCredentialingDataFields
        }
    }
`;

/**
 * Every credentialing consent the provider has given, oldest first: the rescinded ones and
 * the live one as one list, `isActive` marking the one in force. Readable by the provider
 * themselves or an admin with READ_CREDENTIALING_DATA.
 */
export const GET_PROVIDER_CREDENTIALING_CONSENTS = gql`
    query ProviderCredentialingConsents($providerUserId: ID!) {
        providerCredentialingConsents(providerUserId: $providerUserId) {
            consentFullName
            consentDOB
            npi
            caqhIdNumber
            purpose
            consentSignedDate
            signedIPv4
            consentFormDocumentId
            rescindedAt
            rescindedByUserId
            isActive
        }
    }
`;

export const GET_PROVIDERS = gql`
    ${USER_MINI_FRAGMENT}
    query Providers {
        providers {
            users {
                ...userMiniFields
                role {
                    name
                }
                country {
                    currency
                }
            }
            total
        }
    }
`;

/*
 * Admins
 */

export const GET_ADMINS = gql`
    ${USER_MINI_FRAGMENT}
    query Admins {
        admins {
            ...userMiniFields
            role {
                name
            }
        }
    }
`;

export const GET_CAREBOT_BETA_LICENSE_INFO = gql`
    query CarebotBetaLicenseInfo {
        carebotBetaLicenseInfo {
            used
            total
        }
    }
`;

/*
 * Provider
 */

export const GET_PROVIDER_SESSION_TYPES = gql`
    query ProviderSessionTypes($providerUserId: ID!) {
        supportedSessionTypes(providerUserId: $providerUserId)
    }
`;

export const GET_PROVIDER_CASELOAD = gql`
    ${USER_MINI_FRAGMENT}
    query Caseload($providerUserId: ID!) {
        caseload(providerUserId: $providerUserId) {
            ...userMiniFields
            clientProfile {
                coverageType
            }
        }
    }
`;

// NOTE: no consumers; kept slim (userMiniFields) rather than the heavy preview.
/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_PROVIDER_CASELOAD_WITH_APPOINTMENTS = gql`
    ${USER_MINI_FRAGMENT}
    query CaseloadWithAppointments($providerUserId: ID!, $activityWindow: Int!) {
        caseloadWithAppointments(providerUserId: $providerUserId, activityWindow: $activityWindow) {
            client {
                ...userMiniFields
            }
            lastAppointmentDate
        }
    }
`;

export const PROVIDER_CASELOAD_WITH_APPOINTMENTS = gql`
    ${USER_MINI_FRAGMENT}
    query ProviderCaseloadWithAppointments(
        $providerUserId: ID!
        $cursor: String
        $limit: Int
        $searchQuery: String
    ) {
        providerCaseloadWithAppointments(
            providerUserId: $providerUserId
            cursor: $cursor
            limit: $limit
            searchQuery: $searchQuery
        ) {
            edges {
                node {
                    client {
                        ...userMiniFields
                    }
                    lastAppointmentDate
                }
                cursor
            }
            pageInfo {
                hasNextPage
                endCursor
            }
            totalCount
        }
    }
`;

export const GET_PROVIDER_UNBOOKED_CLIENTS = gql`
    ${USER_MINI_FRAGMENT}
    query UnbookedClients($providerUserId: ID!, $activityWindow: Int!) {
        unbookedClients(providerUserId: $providerUserId, activityWindow: $activityWindow) {
            client {
                ...userMiniFields
            }
            lastAppointmentDate
        }
    }
`;

export const GET_PROVIDER_AVAILABILITY_SUMMARY = gql`
    query ProviderAvailabilitySummary($providerUserId: ID!) {
        providerAvailabilitySummary(providerUserId: $providerUserId) {
            currentHoursPerWeek
            targetHoursPerWeek
        }
    }
`;

/**
 * The dashboard To-Do card. Each of the three entries is null when there is nothing to do,
 * so the card hides that row. Readable by the provider themselves only.
 *
 * The two session entries are kept to the fields the card renders rather than the
 * appointment preview fragment — the card only needs a name and a date.
 */
export const GET_PROVIDER_TODOS = gql`
    query ProviderTodos($providerUserId: ID!) {
        providerTodos(providerUserId: $providerUserId) {
            credentialOnboarding {
                percentComplete
                completedSections
                totalSections
                isComplete
                hasExpiredLicense
            }
            manageNotes {
                appointmentId
                startDateTime
                scheduledTimezone
                client {
                    userId
                    name
                    firstName
                    lastName
                }
            }
            manageDiagnosis {
                appointmentId
                startDateTime
                scheduledTimezone
                client {
                    userId
                    name
                    firstName
                    lastName
                }
            }
            feedback {
                groups {
                    key
                    label
                    accountAtRisk
                    items {
                        id
                        text
                        completed
                    }
                }
            }
        }
    }
`;

export const GET_PROVIDER_FEEDBACK_BANNER = gql`
    query ProviderFeedbackBanner($providerUserId: ID!) {
        providerTodos(providerUserId: $providerUserId) {
            feedback {
                bannerState
                booking {
                    startAt
                    endAt
                    rescheduleUrl
                    cancelUrl
                }
            }
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_PROVIDER_PAYOUTS = gql`
    query ProviderPayouts($providerUserId: ID!) {
        payouts(providerUserId: $providerUserId) {
            amount
            currency
            description
            statementDescriptor
            created
            arrivalDate
            status
        }
    }
`;

export const GET_PROVIDER_LISTING_TAGS = gql`
    query ProviderListingTags($providerUserId: ID!) {
        listingTags(providerUserId: $providerUserId) {
            label
            severity
            symbol
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_PROVIDER_REVIEWS = gql`
    query ProviderReviews($providerUserId: ID!) {
        providerReviews(providerUserId: $providerUserId) {
            overallProviderRating
            createdAt
        }
    }
`;

export const GET_PROVIDER_RATING = gql`
    query ProviderRating($providerUserId: ID!) {
        providerRating(providerUserId: $providerUserId) {
            average
            count
        }
    }
`;

/*
 * Client
 */

export const GET_MEDICAL_INTAKE = gql`
    ${MEDICAL_INTAKE_FRAGMENT}
    query MedicalIntake($clientUserId: ID!) {
        medicalIntake(clientUserId: $clientUserId) {
            ...medicalIntakeFields
        }
    }
`;

export const GET_RELEASES = gql`
    ${RELEASE_FRAGMENT}
    query Releases($clientUserId: ID!) {
        releases(clientUserId: $clientUserId) {
            ...releaseFields
        }
    }
`;

export const GET_CLIENT_PLANS = gql`
    ${CLIENT_PLAN_FRAGMENT}
    query ClientPlans($clientUserId: ID!) {
        plans(clientUserId: $clientUserId) {
            ...clientPlanFields
        }
    }
`;

export const GET_CLIENT_BILLING_HISTORY = gql`
    ${PAYMENT_FRAGMENT}

    query ClientBillingHistory($clientUserId: ID!, $types: [PaymentType!]) {
        paymentHistory(clientUserId: $clientUserId, types: $types) {
            ...paymentFields
        }
    }
`;

export const GET_PAYMENTMETHOD_SETUP_INTENT = gql`
    query PaymentMethodSetupIntent($clientUserId: ID!) {
        paymentMethodSetupIntent(clientUserId: $clientUserId) {
            clientSecret
        }
    }
`;

export const GET_PAYMENTMETHODS = gql`
    query PaymentMethods($clientUserId: ID!) {
        paymentMethods(clientUserId: $clientUserId) {
            paymentMethodId
            brand
            last4
            expMonth
            expYear
            isDefault
        }
    }
`;

export const GET_JOURNALS = gql`
    ${JOURNAL_FRAGMENT}
    query Journals($clientUserId: ID!) {
        journals(clientUserId: $clientUserId) {
            ...journalFields
        }
    }
`;

export const GET_PAGINATED_JOURNALS = gql`
    ${JOURNAL_FRAGMENT}
    query PaginatedJournals($clientUserId: ID!, $pagination: PaginationInput) {
        paginatedJournals(clientUserId: $clientUserId, pagination: $pagination) {
            items {
                ...journalFields
            }
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor {
                    id
                    timestamp
                }
                endCursor {
                    id
                    timestamp
                }
            }
            totalCount
        }
    }
`;

export const GET_TODOS = gql`
    query Todos($clientUserId: ID!) {
        todos(clientUserId: $clientUserId) {
            type
            isComplete
        }
    }
`;

/*
 * Organizations
 */

export const SEARCH_ORGANIZATIONS = gql`
    ${ORGANIZATION_PREVIEW_FRAGMENT}
    query Organizations(
        $query: OrganizationSearchQuery
        $offset: Int
        $limit: Int
        $sort: [SortTerm]
    ) {
        organizations(query: $query, offset: $offset, limit: $limit, sort: $sort) {
            total
            organizations {
                ...organizationPreviewFields
            }
        }
    }
`;

export const GET_ORGANIZATION = gql`
    ${ORGANIZATION_FRAGMENT}
    query Organization($organizationId: ID!) {
        organization(organizationId: $organizationId) {
            ...organizationFields
        }
    }
`;

export const SEARCH_ORGANIZATION_MEMBERS = gql`
    ${USER_MINI_FRAGMENT}
    query OrganizationMembers(
        $query: ClientSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        organizationMembers(query: $query, offset: $offset, limit: $limit, sort: $sort) {
            users {
                ...userMiniFields
                phoneNumber
                email
                gender
                createdAt
                role {
                    name
                }
                country {
                    abbreviation
                }
                clientProfile {
                    state
                    State {
                        name
                        abbreviation
                    }
                }
                organizationProfile {
                    status
                    department {
                        name
                    }
                    team {
                        name
                    }
                    location {
                        name
                    }
                }
            }
            total
        }
    }
`;

export const GET_ORGANIZATION_PAYMENTS = gql`
    ${PAYMENT_FRAGMENT}
    query OrganizationPayments($organizationId: ID!) {
        organizationPayments(organizationId: $organizationId) {
            ...paymentFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_ORGANIZATION_MEMBER_UPLOAD_FIELDS = gql`
    query OrganizationMemberUploadFields {
        organizationMemberUploadFields
    }
`;

/*
 * Payer Admin
 */

export const GET_PAYER_ACTIVE_PROVIDER_COUNT = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerActiveProviderCount {
        payerActiveProviderCount {
            current {
                ...metricFields
            }
            previous {
                ...metricFields
            }
            delta {
                ...deltaFields
            }
        }
    }
`;

export const GET_PAYER_PROVIDERS_ACCEPTING_REFERRALS = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerProvidersAcceptingReferrals {
        payerProvidersAcceptingReferrals {
            current {
                ...metricFields
            }
            previous {
                ...metricFields
            }
            delta {
                ...deltaFields
            }
        }
    }
`;

export const GET_PAYER_AVERAGE_TIME_TO_FIRST_APPOINTMENT = gql`
    query PayerAverageTimeToFirstAppointment {
        payerAverageTimeToFirstAppointment {
            value
        }
    }
`;

export const GET_PAYER_PROVIDERS_BY_STATE = gql`
    query PayerProvidersByState {
        payerProvidersByState {
            state
            abbreviation
            availableProviders
            activeProviders
            clientCount
        }
    }
`;

export const GET_PAYER_TIME_TO_FIRST_APPOINTMENT = gql`
    query PayerTimeToFirstAppointment {
        payerTimeToFirstAppointment {
            stats {
                count
                average
                p50
                p95
                p99
                withinTargetPercent
                targetDays
            }
            waitingNow {
                total
                days0to7
                days8to14
                days15to30
                days31to90
            }
            members
        }
    }
`;

export const GET_PAYER_STATE_METRICS = gql`
    query PayerStateMetrics {
        payerStateMetrics {
            state
            abbreviation
            activeLicenses
            acceptingReferrals
            soonestOpening
            stats {
                count
                average
                p50
                p95
                p99
                withinTargetPercent
                targetDays
            }
            waitingNow {
                total
                days0to7
                days8to14
                days15to30
                days31to90
            }
            members
        }
    }
`;

export const GET_PAYER_APPOINTMENT_OUTCOME_RATES = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerAppointmentOutcomeRates($granularity: Granularity!, $date: String) {
        payerAppointmentOutcomeRates(granularity: $granularity, date: $date) {
            showRate {
                current {
                    ...metricFields
                }
                previous {
                    ...metricFields
                }
                delta {
                    ...deltaFields
                }
            }
            noShowRate {
                current {
                    ...metricFields
                }
                previous {
                    ...metricFields
                }
                delta {
                    ...deltaFields
                }
            }
            cancellationRate {
                current {
                    ...metricFields
                }
                previous {
                    ...metricFields
                }
                delta {
                    ...deltaFields
                }
            }
        }
    }
`;

export const GET_PAYER_CLIENTS_BY_CONDITIONS = gql`
    query PayerClientsByConditions($granularity: Granularity!, $date: String) {
        payerClientsByConditions(granularity: $granularity, date: $date) {
            label
            count
            percent
        }
    }
`;

export const GET_PAYER_AVG_CASE_SESSIONS = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerAverageCaseSessions {
        payerAverageCaseSessions {
            current {
                ...metricFields
            }
            previous {
                ...metricFields
            }
            delta {
                ...deltaFields
            }
        }
    }
`;

export const GET_PAYER_CASE_CLOSURE_RATES = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerCaseClosureRates {
        payerCaseClosureRates {
            current {
                ...metricFields
            }
            previous {
                ...metricFields
            }
            delta {
                ...deltaFields
            }
        }
    }
`;

export const GET_PAYER_REPEAT_CLIENT_RATE = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerRepeatClientRate {
        payerRepeatClientRate {
            current {
                ...metricFields
            }
            previous {
                ...metricFields
            }
            delta {
                ...deltaFields
            }
        }
    }
`;

export const GET_PAYER_CLIENT_IMPROVEMENT = gql`
    ${METRIC_FRAGMENT}
    ${DELTA_FRAGMENT}
    query PayerClientImprovement {
        payerClientImprovement {
            clientsShowingImprovement {
                current {
                    ...metricFields
                }
                previous {
                    ...metricFields
                }
                delta {
                    ...deltaFields
                }
            }
            avgSessionsToImprovement {
                current {
                    ...metricFields
                }
                previous {
                    ...metricFields
                }
                delta {
                    ...deltaFields
                }
            }
        }
    }
`;

export const GET_PAYER_CASES = gql`
    query PayerCases {
        payerCases {
            resolved
            ongoing
        }
    }
`;

export const GET_PAYER_CLIENT_ASSESSMENT_PROGRESS = gql`
    query PayerClientAssessmentProgress {
        payerClientAssessmentProgress {
            phq9
            gad7
        }
    }
`;

export const GET_PAYER_PROVIDER_SCORE_DISTRIBUTION = gql`
    query PayerProviderScoreDistribution {
        payerProviderScoreDistribution {
            scores {
                label
                value
            }
            total
        }
    }
`;

export const GET_PAYER_REFERRALS = gql`
    query PayerReferrals($granularity: Granularity!, $date: String) {
        payerReferrals(granularity: $granularity, date: $date) {
            total
            inCare
            scheduled
            completedCare
            awaitingFirstBooking
            didNotEngage
        }
    }
`;

export const GET_PAYER_COSTING = gql`
    query PayerCosting {
        payerCosting {
            costPerCase
            costPerSession
        }
    }
`;

/*
 * Availability
 */

export const GET_ELEMENT_TOKEN = gql`
    query ElementToken($providerUserId: ID!, $origin: String!) {
        cronofyElementToken(providerUserId: $providerUserId, origin: $origin) {
            permissions
            origin
            token
            expires_in
            subs
        }
    }
`;

export const GET_AVAILABILITY_QUERY = gql`
    query AvailabilityQuery(
        $providerUserId: ID!
        $clientUserId: ID!
        $timestamp: String!
        $ignoreDailyLimit: Boolean
        $appointmentId: ID
    ) {
        availability(
            providerUserId: $providerUserId
            clientUserId: $clientUserId
            timestamp: $timestamp
            ignoreDailyLimit: $ignoreDailyLimit
            appointmentId: $appointmentId
        ) {
            emptyFlag
            query {
                participants {
                    required
                    members {
                        sub
                        calendar_ids
                        managed_availability
                        availability_rule_ids
                    }
                }
                required_duration {
                    minutes
                }
                response_format
                start_interval {
                    minutes
                }
                max_results
                query_periods {
                    start
                    end
                }
            }
        }
    }
`;

export const GET_AVAILABLE_PERIODS = gql`
    query AvailablePeriods($providerUserId: ID!, $range: UtcPeriodInput!) {
        availablePeriods(providerUserId: $providerUserId, range: $range) {
            start
            end
        }
    }
`;

export const GET_AVAILABLE_SLOTS = gql`
    query AvailableSlots($providerUserId: ID!, $clientUserId: ID, $range: UtcPeriodInput!) {
        availableSlots(
            providerUserId: $providerUserId
            clientUserId: $clientUserId
            range: $range
        ) {
            start
            end
        }
    }
`;

export const GET_AVAILABILITY_RULE = gql`
    query GetAvailabilityRule($providerUserId: ID!) {
        availabilityRule(providerUserId: $providerUserId) {
            ruleId
            tzid
            weeklyPeriods {
                day
                startTime
                endTime
            }
        }
    }
`;

export const GET_INDIVIDUAL_CONNECT_AUTH_URL = gql`
    query IndividualConnectAuthUrl($providerName: CronofyProviderName!) {
        individualConnectAuthUrl(providerName: $providerName)
    }
`;

export const GET_ACCOUNT_PROFILES = gql`
    query AccountProfiles {
        accountProfiles {
            providerName
            profileId
            profileName
            profileConnected
            profileCalendars {
                calendarId
                calendarName
                calendarPrimary
                calendarDeleted
            }
        }
    }
`;

/*
 * Appointments
 */

export const SEARCH_APPOINTMENTS = gql`
    ${APPOINTMENT_PREVIEW_FRAGMENT}
    query SearchAppointments(
        $searchQuery: AppointmentSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
        $targetTimezone: String
    ) {
        appointments(
            searchQuery: $searchQuery
            offset: $offset
            limit: $limit
            sort: $sort
            targetTimezone: $targetTimezone
        ) {
            appointments {
                ...appointmentPreviewFields
                canShareFeedback
            }
            total
        }
    }
`;

/** Admin appointments export: the appointments search, selecting only the exported columns. */
export const EXPORT_APPOINTMENTS = gql`
    query ExportAppointments(
        $searchQuery: AppointmentSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
        $targetTimezone: String
    ) {
        appointments(
            searchQuery: $searchQuery
            offset: $offset
            limit: $limit
            sort: $sort
            targetTimezone: $targetTimezone
        ) {
            appointments {
                appointmentId
                client {
                    name
                    country {
                        abbreviation
                    }
                }
                provider {
                    name
                }
                startDateTime
                status
                coverageType
            }
            total
        }
    }
`;

/**
 * Server-side bookability check. Replaces the two SEARCH_APPOINTMENTS calls that
 * `useIsClientBookable` previously issued just to compare their totals — each of
 * which loaded the full appointment preview payload (client, provider, booker,
 * policy, diagnosis and every nested profile).
 */
export const IS_CLIENT_BOOKABLE = gql`
    query IsClientBookable($clientId: ID!) {
        isClientBookable(clientId: $clientId)
    }
`;

/**
 * Server-side "has this client ever had an appointment occur with this provider"
 * check. Replaces the SEARCH_APPOINTMENTS call in `useIsNewClient`.
 */
export const IS_NEW_CLIENT = gql`
    query IsNewClient($providerId: ID!, $clientId: ID) {
        isNewClient(providerId: $providerId, clientId: $clientId)
    }
`;

export const GET_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}
    query Appointment($appointmentId: ID!) {
        appointment(appointmentId: $appointmentId) {
            ...appointmentFields
        }
    }
`;

export const GET_NEXT_APPOINTMENT = gql`
    ${APPOINTMENT_FRAGMENT}
    query NextAppointment($startPoint: String, $endPoint: String) {
        nextAppointment(startPoint: $startPoint, endPoint: $endPoint) {
            ...appointmentFields
        }
    }
`;

export const GET_SESSION_DATA = gql`
    ${SESSION_DATA_FRAGMENT}
    query SessionData($appointmentId: ID!) {
        sessionData(appointmentId: $appointmentId) {
            ...sessionDataFields
        }
    }
`;

export const GET_APPOINTMENT_ACTIONS = gql`
    query AppointmentActions($appointmentId: ID!) {
        appointmentActions(appointmentId: $appointmentId) {
            type
            options {
                type
                default
                visibleTo
            }
        }
    }
`;

export const GET_BOOKING_OPTIONS = gql`
    query BookingOptions {
        bookingOptions {
            type
            default
            visibleTo
        }
    }
`;

export const GET_OVERRIDE_STATUSES = gql`
    query OverrideStatuses($appointmentId: ID!) {
        overrideStatuses(appointmentId: $appointmentId)
    }
`;

export const GET_APPOINTMENT_BILLING_HISTORY = gql`
    ${PAYMENT_FRAGMENT}

    query AppointmentBillingHistory($appointmentId: ID!) {
        payments(appointmentId: $appointmentId) {
            ...paymentFields
        }
    }
`;

/*
 * Assessments
 */

export const GET_ASSESSMENTS = gql`
    ${ASSESSMENT_FRAGMENT}
    query Assessments($clientUserId: ID!) {
        assessments(clientUserId: $clientUserId) {
            ...assessmentFields
        }
    }
`;

/*
 * Claims
 */

export const GET_INSURANCE_CLAIM = gql`
    ${INSURANCE_CLAIM_FRAGMENT}
    query InsuranceClaim($insuranceClaimId: ID!) {
        insuranceClaim(insuranceClaimId: $insuranceClaimId) {
            ...insuranceClaimFields
        }
    }
`;

export const SEARCH_INSURANCE_CLAIMS = gql`
    ${INSURANCE_CLAIM_FRAGMENT}
    query SearchInsuranceClaims(
        $searchQuery: ClaimSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        insuranceClaims(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            claims {
                ...insuranceClaimFields
            }
            total
        }
    }
`;

/**
 * Admin claims export: the claims search, selecting only the exported columns
 * (no member DOB, sex or assessment scores, which the grid fragment carries).
 */
export const EXPORT_INSURANCE_CLAIMS = gql`
    query ExportInsuranceClaims(
        $searchQuery: ClaimSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        insuranceClaims(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            claims {
                insuranceClaimId
                encounterId
                encounter {
                    __typename
                    ... on AppointmentPreview {
                        sessionNumber
                        provider {
                            name
                        }
                        client {
                            name
                        }
                        diagnosis {
                            diagnosisCodes
                        }
                    }
                    ... on Assessment {
                        client {
                            name
                        }
                    }
                }
                policy {
                    memberId
                    authorizationNumber
                }
                insuranceCarrier {
                    name
                }
                payerId
                cptCode
                dateOfService
                dateBilled
                datePaid
                copay
                insAmountPaid
                hsaAmountPaid
                status
                state
                trackingNumber
            }
            total
        }
    }
`;

/*
 * Payouts
 */

export const GET_MASTER_BALANCE = gql`
    query MasterBalance {
        masterBalance {
            available {
                amount
                currency
            }
            instantAvailable {
                amount
                currency
            }
            pending {
                amount
                currency
            }
        }
    }
`;

export const SEARCH_PAYOUTS = gql`
    ${PAYOUT_FRAGMENT}
    query SearchPayouts(
        $searchQuery: PayoutSearchQuery!
        $offset: Int!
        $limit: Int!
        $sort: [SortTerm]
    ) {
        payouts(searchQuery: $searchQuery, offset: $offset, limit: $limit, sort: $sort) {
            payouts {
                ...payoutFields
            }
            total
        }
    }
`;

export const GET_PAYOUT_APPOINTMENTS = gql`
    ${APPOINTMENT_PREVIEW_FRAGMENT}
    query PayoutAppointments($payoutId: ID!) {
        payoutAppointments(payoutId: $payoutId) {
            ...appointmentPreviewFields
        }
    }
`;

export const GET_PAYOUT_GROUP_SESSIONS = gql`
    ${GROUP_SESSION_PREVIEW_FRAGMENT}
    query PayoutGroupSessions($payoutId: ID!) {
        payoutGroupSessions(payoutId: $payoutId) {
            ...groupSessionPreviewFields
            centsPaidOut
        }
    }
`;

/*
 * Assignments
 */

export const GET_COMPLETED_ASSIGNMENTS = gql`
    query CompletedAssignments($clientId: ID!) {
        completedAssignments(clientId: $clientId)
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_ASSIGNMENTS = gql`
    query Assignments($filters: AssignmentFilterInput, $pagination: AssignmentPaginationInput) {
        assignments(filters: $filters, pagination: $pagination) {
            items {
                assignmentId
                name
                slug
                isActive
            }
            pageInfo {
                hasNextPage
                hasPreviousPage
                endCursor {
                    id
                    timestamp
                }
            }
            totalCount
        }
    }
`;

export const PROVIDER_CLIENT_ASSIGNMENTS = gql`
    ${CLIENT_ASSIGNMENT_FRAGMENT}
    query ProviderClientAssignments(
        $pagination: ClientAssignmentPaginationInput
        $filters: ClientAssignmentFilterInput
        $sort: ClientAssignmentSortInput
    ) {
        providerClientAssignments(pagination: $pagination, filters: $filters, sort: $sort) {
            items {
                ...clientAssignmentFields
            }
            totalCount
            limit
            offset
        }
    }
`;

export const PROVIDER_CLIENT_ASSIGNMENT_SUMMARIES = gql`
    query ProviderClientAssignmentSummaries(
        $limit: Int
        $offset: Int
        $sort: ClientAssignmentSummarySortInput
    ) {
        providerClientAssignmentSummaries(limit: $limit, offset: $offset, sort: $sort) {
            items {
                clientId
                client {
                    userId
                    name
                    firstName
                    lastName
                    photoUrl
                }
                totalAssigned
                completedCount
                progress
                lastSessionAt
            }
            totalCount
            limit
            offset
        }
    }
`;

export const PROVIDER_CLIENT_ASSIGNMENTS_FOR_CLIENT = gql`
    ${CLIENT_ASSIGNMENT_FRAGMENT}
    query ProviderClientAssignmentsForClient(
        $clientId: ID!
        $pagination: ClientAssignmentPaginationInput
        $filters: ClientAssignmentFilterInput
        $sort: ClientAssignmentSortInput
    ) {
        providerClientAssignmentsForClient(
            clientId: $clientId
            pagination: $pagination
            filters: $filters
            sort: $sort
        ) {
            items {
                ...clientAssignmentFields
            }
            totalCount
            limit
            offset
        }
    }
`;

export const MY_ASSIGNED_ASSIGNMENTS = gql`
    ${CLIENT_ASSIGNMENT_FRAGMENT}
    query MyAssignedAssignments(
        $pagination: ClientAssignmentPaginationInput
        $filters: ClientAssignmentFilterInput
    ) {
        myAssignedAssignments(pagination: $pagination, filters: $filters) {
            items {
                ...clientAssignmentFields
            }
            totalCount
            limit
            offset
        }
    }
`;

export const GET_ASSIGNMENT_SUBMISSIONS = gql`
    ${ASSIGNMENT_SUBMISSION_FRAGMENT}
    query AssignmentSubmissions(
        $clientId: ID!
        $limit: Int
        $offset: Int
        $sort: String
        $withInference: Boolean = false
    ) {
        assignmentSubmissions(clientId: $clientId, limit: $limit, offset: $offset, sort: $sort) {
            ...assignmentSubmissionFields
        }
    }
`;

export const GET_ASSIGNMENT_SUBMISSION = gql`
    ${ASSIGNMENT_SUBMISSION_FRAGMENT}
    query AssignmentSubmission($submissionId: ID!, $withInference: Boolean = false) {
        assignmentSubmission(submissionId: $submissionId) {
            ...assignmentSubmissionFields
        }
    }
`;

export const GET_PAGINATED_ASSIGNMENT_SUBMISSIONS = gql`
    ${PAGINATED_ASSIGNMENT_SUBMISSIONS_FRAGMENT}
    query PaginatedAssignmentSubmissions(
        $clientId: ID!
        $pagination: AssignmentPaginationInput
        $withInference: Boolean = false
    ) {
        paginatedAssignmentSubmissions(clientId: $clientId, pagination: $pagination) {
            ...paginatedAssignmentSubmissionsFields
        }
    }
`;

export const GET_ASSIGNMENT_INFERENCE = gql`
    query AssignmentInference($submissionId: ID!) {
        assignmentInference(submissionId: $submissionId) {
            assignmentInferenceId
            status
            title
            summary
            keyInsights {
                body
                sortOrder
            }
            emotions {
                emotionText
                emoji
                sortOrder
            }
            suggestedGoals {
                assignmentInferenceGoalSuggestionId
                goalType
                body
                acceptedGoalId
                acceptedGoal {
                    goalId
                    body
                    type
                    status
                }
            }
            patterns {
                body
                sortOrder
            }
            createdAt
        }
    }
`;

export const GET_OWED_PAYOUT_INFO = gql`
    ${OWED_PAYOUT_INFO_FRAGMENT}
    query OwedPayoutInfo($countryId: ID!, $ranges: AppointmentPayoutRangeMap!) {
        owedPayoutInfo(countryId: $countryId, ranges: $ranges) {
            ...owedPayoutInfoFields
        }
    }
`;

// The appointment-level detail is opt-in via `$expanded` (default false). The
// dashboard payout widgets omit it (minimal payload, they only read
// `appointments.length`); the admin drill-in grid passes `expanded: true` to
// fetch the full appointmentPreview for its columns.
export const GET_OWED_PAYOUT_INFO_BREAKDOWN = gql`
    ${OWED_PAYOUT_INFO_BREAKDOWN_FRAGMENT}
    query OwedPayoutInfoBreakdown(
        $providerUserId: ID!
        $ranges: AppointmentPayoutRangeMap
        $expanded: Boolean = false
    ) {
        owedPayoutInfoBreakdown(providerUserId: $providerUserId, ranges: $ranges) {
            ...owedPayoutInfoBreakdownFields
        }
    }
`;

export const GET_ALL_TIME_OWED_PAYOUT_INFO_BREAKDOWN = gql`
    ${OWED_PAYOUT_INFO_BREAKDOWN_FRAGMENT}
    query OwedPayoutInfoBreakdown($providerUserId: ID!, $expanded: Boolean = false) {
        owedPayoutInfoBreakdown(providerUserId: $providerUserId) {
            ...owedPayoutInfoBreakdownFields
        }
    }
`;

export const GET_PAYOUT_JOB_STATUS = gql`
    ${OWED_PAYOUT_INFO_FRAGMENT}
    query PayoutJobStatus($jobId: String!) {
        payoutJobStatus(jobId: $jobId) {
            status
            results {
                ...owedPayoutInfoFields
            }
            error
        }
    }
`;

/*
 * Diagnoses
 */

export const GET_DIAGNOSIS = gql`
    ${DIAGNOSIS_FRAGMENT}
    query Diagnosis($appointmentId: ID!) {
        diagnosis(appointmentId: $appointmentId) {
            ...diagnosisFields
        }
    }
`;

export const GET_DIAGNOSES = gql`
    ${DIAGNOSIS_WITH_APPT_FRAGMENT}
    query Diagnoses($clientId: ID!, $offset: Int!, $limit: Int!, $sort: String!) {
        diagnoses(clientId: $clientId, offset: $offset, limit: $limit, sort: $sort) {
            ...diagnosisWithApptFields
        }
    }
`;
export const GET_DX_CODE = gql`
    ${DIAGNOSIS_CODE_FRAGMENT}
    query DxCode($code: String!) {
        diagnosisCode(code: $code) {
            ...diagnosisCodeFields
        }
    }
`;

/*
 * Conversations
 */

export const GET_CONVERSATION_PREVIEWS = gql`
    ${CONVERSATION_PREVIEW_FRAGMENT}
    query ConversationPreviews($userId: ID!) {
        conversations(userId: $userId) {
            ...conversationPreviewFields
        }
    }
`;

export const GET_CONVERSATION = gql`
    ${CONVERSATION_FRAGMENT}
    query Conversation($conversationId: ID, $userId: ID, $name: String, $offset: Int, $limit: Int) {
        conversation(
            conversationId: $conversationId
            userId: $userId
            name: $name
            offset: $offset
            limit: $limit
        ) {
            ...conversationFields
        }
    }
`;

export const GET_LAST_MESSAGE = gql`
    ${MESSAGE_FRAGMENT}
    query LastMessage($conversationId: ID!, $outboundOnly: Boolean) {
        lastMessage(conversationId: $conversationId, outboundOnly: $outboundOnly) {
            ...messageFields
        }
    }
`;

/*
 * Documents
 */

export const GET_DOCUMENTS = gql`
    ${DOCUMENT_FRAGMENT}
    query Documents($userId: ID!) {
        documents(userId: $userId) {
            ...documentFields
        }
    }
`;

/*
 * Analytics
 */

export const GET_PROVIDER_RETENTION_BREAKDOWN = gql`
    query ProviderRetentionBreakdown($providerUserId: ID!) {
        providerRetentionBreakdown(providerUserId: $providerUserId) {
            totalCustomers
            secondSessionCustomers
            sixthSessionCustomers
            twelfthSessionCustomers
            eighteenthSessionCustomers
        }
    }
`;

export const GET_PROVIDER_RETENTION_AVERAGE = gql`
    query ProviderRetentionAverage($providerUserId: ID!, $inflated: Boolean) {
        providerRetentionAverage(providerUserId: $providerUserId, inflated: $inflated) {
            average
            previousAverage
            percentChange
        }
    }
`;

export const GET_PROVIDER_SESSION_COUNT = gql`
    query ProviderSessionCount($providerUserId: ID!, $rangeStart: String!, $rangeEnd: String!) {
        providerSessionCount(
            providerUserId: $providerUserId
            rangeStart: $rangeStart
            rangeEnd: $rangeEnd
        )
    }
`;

export const GET_PROVIDER_NOSHOW_DETAILS = gql`
    query ProviderNoshowDetails($providerUserId: ID!) {
        providerNoshowDetails(providerUserId: $providerUserId) {
            totalAppointments
            noshowAppointments
            lastMonthsAppointments
            lastMonthsNoshowAppointments
        }
    }
`;

export const GET_PROVIDER_OUTSTANDING_NOTE_DETAILS = gql`
    query ProviderOutstandingNoteDetails($providerUserId: ID!, $rangeStart: String!) {
        providerOutstandingNoteDetails(providerUserId: $providerUserId, rangeStart: $rangeStart) {
            totalNotes
            outstandingNotes
            previousOutstandingNotes
            percentChange
        }
    }
`;

export const GET_PROVIDER_NOTE_COMPLETION_DETAILS = gql`
    query ProviderNoteCompletionDetails($providerUserId: ID!) {
        providerNoteCompletionDetails(providerUserId: $providerUserId) {
            alltimeCompletedNotes
            alltimeCompletionSeconds
            lastMonthsCompletedNotes
            lastMonthsCompletionSeconds
        }
    }
`;

export const GET_PROVIDER_ON_TIME_DETAILS = gql`
    query ProviderOnTimeDetails($providerUserId: ID!) {
        providerOnTimeDetails(providerUserId: $providerUserId) {
            totalSessions
            onTimeSessions
            lastMonthsSessions
            lastMonthsOnTimeSessions
            sinceDate
        }
    }
`;

export const GET_PROVIDER_TARDINESS_DETAILS = gql`
    query ProviderTardinessDetails($providerUserId: ID!) {
        providerTardinessDetails(providerUserId: $providerUserId) {
            alltimeLateSessions
            alltimeLateSeconds
            lastMonthsLateSessions
            lastMonthsLateSeconds
            sinceDate
        }
    }
`;

export const GET_PROVIDER_RESCHEDULE_DETAILS = gql`
    query ProviderRescheduleDetails($providerUserId: ID!) {
        providerRescheduleDetails(providerUserId: $providerUserId) {
            totalAppointments
            rescheduleAppointments
            lastMonthsAppointments
            lastMonthsRescheduleAppointments
        }
    }
`;

export const GET_PROVIDER_REFERRAL_DETAILS = gql`
    query ProviderReferralDetails($providerUserId: ID!) {
        providerReferralDetails(providerUserId: $providerUserId) {
            pending
            paused
            canceled
            customer
            previousTotal
            percentChange
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_PROVIDER_CLIENT_ON_TIME_RATE = gql`
    query ProviderClientOnTimeRate($providerUserId: ID!, $window: UtcPeriodInput) {
        providerClientOnTimeRate(providerUserId: $providerUserId, window: $window) {
            percent
            numerator
            denominator
            window {
                start
                end
            }
            previousPercent
            percentChange
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_PROVIDER_CLIENT_NO_SHOW_RATE = gql`
    query ProviderClientNoShowRate($providerUserId: ID!, $window: UtcPeriodInput) {
        providerClientNoShowRate(providerUserId: $providerUserId, window: $window) {
            percent
            numerator
            denominator
            window {
                start
                end
            }
            previousPercent
            percentChange
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_PROVIDER_CLIENT_RESCHEDULE_RATE = gql`
    query ProviderClientRescheduleRate($providerUserId: ID!, $window: UtcPeriodInput) {
        providerClientRescheduleRate(providerUserId: $providerUserId, window: $window) {
            percent
            numerator
            denominator
            window {
                start
                end
            }
            previousPercent
            percentChange
        }
    }
`;

export const GET_PROVIDER_ASSESSMENT_DETAILS = gql`
    query ProviderAssessmentDetails($providerUserId: ID!) {
        providerAssessmentDetails(providerUserId: $providerUserId) {
            aggregate {
                type
                scores
            }
            clients {
                clientName
                assessments {
                    type
                    scores
                }
            }
        }
    }
`;

export const GET_PROVIDER_CLIENT_INSIGHTS = gql`
    query ProviderInsights($providerUserId: ID!, $limit: Int) {
        providerInsights(providerUserId: $providerUserId, limit: $limit) {
            providerInsightId
            type
            percentage
            adjective
            metadata
            generatedAt
        }
    }
`;

export const GET_PROVIDER_CLIENTS_BY_AGE_GROUP = gql`
    query ProviderClientsByAgeGroup($providerUserId: ID!) {
        providerClientsByAgeGroup(providerUserId: $providerUserId) {
            label
            count
            percent
        }
    }
`;

export const GET_PROVIDER_CLIENTS_BY_CONDITION = gql`
    query ProviderClientsByCondition($providerUserId: ID!) {
        providerClientsByCondition(providerUserId: $providerUserId) {
            label
            count
            percent
        }
    }
`;

export const GET_PROVIDER_MONTHLY_ENGAGEMENT = gql`
    query ProviderMonthlyEngagement($providerUserId: ID!, $window: ProviderEngagementWindowInput) {
        providerMonthlyEngagement(providerUserId: $providerUserId, window: $window) {
            window {
                start
                end
            }
            appointmentsBooked {
                count
                target
                percent
            }
            sessionsCompleted {
                count
                target
                percent
            }
            availabilitySet {
                count
                target
                percent
            }
            carebotMessages {
                count
                target
                percent
            }
            overallPercent
        }
    }
`;

export const GET_PROVIDER_REVENUE_SERIES = gql`
    query ProviderRevenueSeries($providerUserId: ID!, $unit: RevenueUnit!, $anchorDate: String) {
        providerRevenueSeries(
            providerUserId: $providerUserId
            unit: $unit
            anchorDate: $anchorDate
        ) {
            unit
            anchorDate
            currentPeriodRevenue
            previousPeriodRevenue
            percentChange
            points {
                periodStart
                periodEnd
                periodLabel
                revenue
            }
        }
    }
`;

export const GET_ORGANIZATION_MEMBER_BREAKDOWN = gql`
    query OrganizationMemberBreakdown($organizationId: ID!) {
        organizationMemberBreakdown(organizationId: $organizationId) {
            total
            unregistered
            registered
            active
        }
    }
`;

export const GET_UPCOMING_ORGANIZATION_APPOINTMENT_BREAKDOWN = gql`
    query UpcomingOrganizationAppointmentBreakdown($organizationId: ID!, $rangeEnd: String) {
        upcomingOrganizationAppointmentBreakdown(
            organizationId: $organizationId
            rangeEnd: $rangeEnd
        ) {
            total
            eap
            commercial
        }
    }
`;

export const GET_ORGANIZATION_SESSION_DELIVERY_AGGREGATE = gql`
    query OrganizationSessionDeliveryAggregate($organizationId: ID!) {
        organizationSessionDeliveryAggregate(organizationId: $organizationId) {
            oneMonth {
                total
                eap
                commercial
                noShow
            }
            threeMonths {
                total
                eap
                commercial
                noShow
            }
            sixMonths {
                total
                eap
                commercial
                noShow
            }
            yearToDate {
                total
                eap
                commercial
                noShow
            }
            lastYear {
                total
                eap
                commercial
                noShow
            }
            allTime {
                total
                eap
                commercial
                noShow
            }
        }
    }
`;

export const GET_ORGANIZATION_SESSION_DELIVERY_BY_GROUP = gql`
    query OrganizationSessionDeliveryByGroup(
        $organizationId: ID!
        $startDate: String!
        $interval: String
    ) {
        organizationSessionDeliveryByGroup(
            organizationId: $organizationId
            startDate: $startDate
            interval: $interval
        ) {
            all {
                label
                value
            }
            department {
                name
                data {
                    label
                    value
                }
            }
            team {
                name
                data {
                    label
                    value
                }
            }
            location {
                name
                data {
                    label
                    value
                }
            }
        }
    }
`;

export const GET_ORGANIZATION_ASSESSMENT_DATA_BY_GROUP = gql`
    query OrganizationAssessmentDataByGroup(
        $organizationId: ID!
        $assessmentType: String!
        $startDate: String!
        $interval: String
    ) {
        organizationAssessmentDataByGroup(
            organizationId: $organizationId
            assessmentType: $assessmentType
            startDate: $startDate
            interval: $interval
        ) {
            all {
                label
                value
            }
            department {
                name
                data {
                    label
                    value
                }
            }
            team {
                name
                data {
                    label
                    value
                }
            }
            location {
                name
                data {
                    label
                    value
                }
            }
        }
    }
`;

// type QuestionnaireProfileLikertAnswer {
//     answer: QuestionnaireQuestionAnswer
//     count: Int
// }

// type QuestionnaireProfileLikert {
//     question: QuestionnaireQuestion
//     answers: [QuestionnaireProfileLikertAnswer]
//     total: Int
// }

export const GET_ORGANIZATION_QUESTIONNAIRE_DATA = gql`
    ${QUESTIONNAIRE_QUESTION_FRAGMENT}
    query OrganizationQuestionnaireData(
        $organizationId: ID!
        $questionnaireName: String
        $questionnaireId: ID
        $dateRange: DateRange!
    ) {
        organizationQuestionnaireData(
            organizationId: $organizationId
            questionnaireName: $questionnaireName
            questionnaireId: $questionnaireId
            dateRange: $dateRange
        ) {
            questions {
                ...questionnaireQuestionFields
            }
            binary {
                question {
                    ...questionnaireQuestionFields
                }
                total
                yes
                no
            }
            likert {
                question {
                    ...questionnaireQuestionFields
                }
                answers {
                    answer {
                        answerId
                        text
                        interpretation
                        label
                    }
                    count
                }
                total
            }
        }
    }
`;

/*
 * Test Session
 */

export const GET_TEST_SESSION_TOKEN = gql`
    query GetTestSessionToken($platform: String) {
        getTestSessionToken(platform: $platform) {
            token
        }
    }
`;

export const GET_GROUP_SESSION_ACTIVE_PARTICIPANT_COUNT = gql`
    query GetGroupSessionActiveParticipantCount($groupSessionId: Int!) {
        groupSessionActiveParticipantCount(groupSessionId: $groupSessionId)
    }
`;

/*
 * Trusted Users
 */
export const GET_PENDING_TRUSTED_USERS = gql`
    query GetInvitedGuests($userId: ID!, $isPending: Boolean) {
        getInvitedGuests(userId: $userId, isPending: $isPending) {
            userId
            contactId
            firstName
            lastName
            phoneNumber
            email
            relationship
            consentForContact
            isPending
            createdAt
            attachToAppointment
            note
        }
    }
`;

export const GET_TRUSTED_USERS = gql`
    query GetTrustedUsers($userId: ID!) {
        getTrustedUsers(userId: $userId) {
            userId
            name
            firstName
            lastName
            email
            phoneNumber
        }
    }
`;

export const GET_SUGGESTED_GUESTS_FOR_APPOINTMENT = gql`
    query GetSuggestedGuests($clientId: ID!, $providerId: ID!) {
        suggestedGuests(clientId: $clientId, providerId: $providerId) {
            userId
            name
            firstName
            lastName
            email
        }
    }
`;

export const GET_GUESTS_FOR_APPOINTMENT = gql`
    ${USER_MINI_FRAGMENT}
    query GetAppointmentWithGuests($appointmentId: ID!) {
        appointment(appointmentId: $appointmentId) {
            guests {
                ...userMiniFields
            }
        }
    }
`;

/*
 * Identity utilities
 */
export const IS_EMAIL_OR_PHONE_TAKEN = gql`
    query CheckEmailTaken($email: String, $phoneNumber: String) {
        isEmailOrPhoneTaken(email: $email, phoneNumber: $phoneNumber)
    }
`;

/*
 * Medical Fax
 */
export const GET_PATIENT_MEDICAL_DOCUMENTS = gql`
    ${MEDICAL_DOCUMENT_FRAGMENT}
    query GetPatientMedicalDocuments($clientUserId: ID!) {
        clientMedicalDocuments(clientUserId: $clientUserId) {
            ...medicalDocumentFields
        }
    }
`;

export const GET_CLIENT_FAX_HISTORY = gql`
    ${FAX_JOB_FRAGMENT}
    query GetClientFaxHistory($clientId: ID!, $senderId: ID) {
        clientFaxHistory(clientId: $clientId, senderId: $senderId) {
            ...faxJobFields
        }
    }
`;

export const GET_FAX_DOCUMENT_DOWNLOAD_URL = gql`
    query GetFaxDocumentDownloadUrl($faxJobId: ID!) {
        getFaxDocumentDownloadUrl(faxJobId: $faxJobId)
    }
`;

export const GET_ADMIN_FAX_JOBS = gql`
    query AdminFaxJobs($filter: FaxJobFilter, $limit: Int, $offset: Int) {
        adminFaxJobs(filter: $filter, limit: $limit, offset: $offset) {
            faxJobs {
                faxJobId
                status
                createdAt
                updatedAt
                completedAt
                recipientFax
                requestedById
                requestedBy {
                    firstName
                    lastName
                    name
                }
                errorMessage
                s3Key
                s3Bucket
                documentSnapshot {
                    id
                    type
                    title
                }
                jobHistory {
                    id
                    type
                    status
                    startedAt
                    completedAt
                    errorMessage
                    metadata {
                        pageCount
                        fileSizeBytes
                        transmissionDuration
                        confirmationId
                        errorType
                        errorCode
                        failureStep
                    }
                }
            }
            totalCount
        }
    }
`;

export const GET_FAX_DOWNLOAD_URL = gql`
    query GetFaxDocumentDownloadUrl($faxJobId: ID!) {
        getFaxDocumentDownloadUrl(faxJobId: $faxJobId)
    }
`;

/*
 * Waiting Room Queries
 */

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_CLIENT_ENGAGEMENT_DATA = gql`
    query GetClientEngagementData($clientId: Int!, $providerId: Int!) {
        clientEngagementData(clientId: $clientId, providerId: $providerId) {
            assignments {
                id
                title
                assignedDate
                status
                dueDate
            }
            groups {
                id
                name
                dateJoined
                sessionsAttended
                lastAttended
            }
            carebotConversations {
                id
                title
                date
                summary
            }
            assessments {
                id
                title
                date
                score
                severity
            }
            wellnessContent {
                id
                title
                type
                duration
                color
            }
        }
    }
`;

export const GET_MOOD_CALENDAR = gql`
    query GetMoodCalendar($userId: ID!, $startDateTime: String!, $endDateTime: String!) {
        getMoodCalendar(userId: $userId, startDateTime: $startDateTime, endDateTime: $endDateTime) {
            startDate
            endDate
            days {
                date
                mood
            }
        }
    }
`;

export const GET_SESSION_HISTORY = gql`
    query GetSessionHistory($clientId: Int!, $providerId: Int!, $limit: Int, $offset: Int) {
        sessionHistory(
            clientId: $clientId
            providerId: $providerId
            limit: $limit
            offset: $offset
        ) {
            total
            sessions {
                appointmentId
                sessionNumber
                sessionDate
                duration
                sessionType
                note
                recap
                patterns {
                    type
                    title
                    description
                }
                suggestedDiscussionPoints {
                    topic
                    rationale
                    priority
                }
                diagnosis {
                    subjectiveNote
                    objectiveNote
                    assessmentNote
                    planNote
                    additionalNote
                    treatmentRecommendation
                    diagnosisCodes
                    areNotesLocked
                    dateNotesLocked
                }
                sessionData {
                    transcription
                    duration
                }
            }
        }
    }
`;

export const GET_SESSION_RECAP_AND_PATTERNS = gql`
    query GetSessionRecapAndPatterns($appointmentId: ID!) {
        sessionRecapAndPatterns(appointmentId: $appointmentId) {
            recap
            patterns {
                type
                title
                description
            }
            suggestedDiscussionPoints {
                topic
                rationale
                priority
            }
        }
    }
`;

export const GET_CAREBOT_CONVERSATIONS = gql`
    query GetCarebotConversations($clientUserId: ID!) {
        carebotConversations(clientUserId: $clientUserId) {
            id
            title
            date
            summary
        }
    }
`;

export const GET_CAREBOT_CONVERSATION_MESSAGES = gql`
    query GetCarebotConversationMessages($conversationId: ID!, $limit: Int) {
        conversationMessages(conversationId: $conversationId, limit: $limit) {
            messageId
            role
            content
            createdAt
        }
    }
`;

export const GET_ASSESSMENTS_COMPLETED = gql`
    query GetAssessmentsCompleted($clientUserId: ID!, $fromDate: String, $toDate: String) {
        assessmentsCompleted(clientUserId: $clientUserId, fromDate: $fromDate, toDate: $toDate) {
            id
            title
            date
            score
            severity
        }
    }
`;

export const GET_INFERRED_GOALS = gql`
    query GetInferredGoals($journalId: ID!) {
        getInferredGoals(journalId: $journalId) {
            goalId
            userId
            journalId
            body
            type
            status
            visibility
            createdAt
            updatedAt
        }
    }
`;

/**
 * Groups
 */

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_GROUP_PREVIEW = gql`
    ${GROUP_PREVIEW_FRAGMENT}
    query Group($groupId: Int!) {
        group(groupId: $groupId) {
            ...groupPreviewFields
        }
    }
`;

export const GET_GROUP_CLIENT_PREVIEW = gql`
    ${GROUP_CLIENT_PREVIEW_FRAGMENT}
    query Group($groupId: Int!) {
        group(groupId: $groupId) {
            ...groupClientPreviewFields
        }
    }
`;

export const GET_PAGINATED_GROUPS_CLIENT_PREVIEW = gql`
    ${GROUP_CLIENT_PREVIEW_CONNECTION_FRAGMENT}
    query PaginatedGroupsClientPreview(
        $filters: GroupSearchFiltersInput
        $pagination: GroupPaginationInput
    ) {
        paginatedGroups(filters: $filters, pagination: $pagination) {
            ...groupClientPreviewConnectionFields
        }
    }
`;

export const GET_GROUP = gql`
    ${GROUP_FRAGMENT}
    query Group($groupId: Int!) {
        group(groupId: $groupId) {
            ...groupFields
        }
    }
`;

export const GET_PAGINATED_GROUPS = gql`
    ${GROUP_CONNECTION_FRAGMENT}
    query PaginatedGroups($filters: GroupSearchFiltersInput, $pagination: GroupPaginationInput) {
        paginatedGroups(filters: $filters, pagination: $pagination) {
            ...groupConnectionFields
        }
    }
`;

export const GET_PAGINATED_GROUPS_WITH_ANALYTICS = gql`
    ${GROUP_CONNECTION_WITH_ANALYTICS_FRAGMENT}
    query PaginatedGroups($filters: GroupSearchFiltersInput, $pagination: GroupPaginationInput) {
        paginatedGroups(filters: $filters, pagination: $pagination) {
            ...groupConnectionWithAnalyticsFields
        }
    }
`;

export const GET_PAGINATED_GROUPS_WITH_SESSIONS = gql`
    ${GROUP_CONNECTION_WITH_SESSIONS_FRAGMENT}
    query PaginatedGroupsWithSessions(
        $filters: GroupSearchFiltersInput
        $pagination: GroupPaginationInput
    ) {
        paginatedGroups(filters: $filters, pagination: $pagination) {
            ...groupConnectionWithSessionsFields
        }
    }
`;

export const GET_TOPICS = gql`
    ${TOPIC_FRAGMENT}
    query Topics($filter: TopicFilter) {
        topics(filter: $filter) {
            ...topicFields
        }
    }
`;

export const GET_GROUPS_STATS = gql`
    ${GROUPS_STATS_FRAGMENT}
    query GroupsStats {
        groupsStats {
            ...groupStatsFields
        }
    }
`;

export const GET_GROUP_SESSION = gql`
    ${GROUP_SESSION_FRAGMENT}
    query GroupSession($groupSessionId: Int!) {
        groupSession(groupSessionId: $groupSessionId) {
            ...groupSessionFields
        }
    }
`;

export const GET_GROUP_SESSIONS = gql`
    ${GROUP_SESSIONS_CONNECTION_FRAGMENT}
    query GroupSessions(
        $filters: GroupSessionSearchFiltersInput
        $pagination: GroupSessionPaginationInput
    ) {
        groupSessions(filters: $filters, pagination: $pagination) {
            ...groupSessionsConnectionFields
        }
    }
`;

export const GET_GROUP_SESSIONS_PREVIEW = gql`
    ${GROUP_SESSION_PREVIEW_CONNECTION_FRAGMENT}
    query GroupSessionsPreview(
        $filters: GroupSessionSearchFiltersInput
        $pagination: GroupSessionPaginationInput
    ) {
        groupSessions(filters: $filters, pagination: $pagination) {
            ...groupSessionPreviewConnectionFields
            items {
                canShareFeedback
            }
        }
    }
`;

export const GET_GROUP_SESSION_RSVPS = gql`
    ${GROUP_SESSION_RSVP_FRAGMENT}
    query GroupSessionRsvps($groupSessionId: Int!) {
        groupSessionRsvps(groupSessionId: $groupSessionId) {
            ...groupSessionRsvpFields
        }
    }
`;

export const GET_GROUP_PLATFORM_SUBSCRIPTION = gql`
    ${GROUPS_SUBSCRIPTION_FRAGMENT}
    query GroupPlatformSubscription($userId: Int) {
        groupPlatformSubscription(userId: $userId) {
            ...groupsSubscriptionFields
        }
    }
`;

export const GET_AVAILABLE_SUBSCRIPTION_PLANS = gql`
    ${AVAILABLE_SUBSCRIPTION_PLAN_FRAGMENT}
    query AvailableSubscriptionPlans {
        availableSubscriptionPlans {
            ...availableSubscriptionPlanFields
        }
    }
`;

export const GET_GROUP_PLATFORM_CONFIG = gql`
    ${GROUP_PLATFORM_CONFIG_FRAGMENT}
    query GroupPlatformConfig {
        groupPlatformConfig {
            ...groupPlatformConfigFields
        }
    }
`;

export const GET_PAGINATED_GROUP_MEMBERS = gql`
    ${GROUP_MEMBER_CONNECTION_FRAGMENT}
    query PaginatedGroupMembers(
        $filters: PaginatedGroupMembersFiltersInput!
        $pagination: GroupMemberPaginationInput!
    ) {
        paginatedGroupMembers(filters: $filters, pagination: $pagination) {
            ...groupMemberConnectionFields
        }
    }
`;

export const GET_PAGINATED_ATTENDANCE = gql`
    ${ATTENDANCE_RECORD_CONNECTION_FRAGMENT}
    query PaginatedAttendance(
        $filters: PaginatedAttendanceFiltersInput!
        $pagination: GroupSessionPaginationInput
    ) {
        paginatedAttendance(filters: $filters, pagination: $pagination) {
            ...attendanceRecordConnectionFields
        }
    }
`;

export const GET_PAGINATED_GROUP_MEMBERSHIP_EVENTS = gql`
    ${GROUP_MEMBERSHIP_EVENT_CONNECTION_FRAGMENT}
    query PaginatedGroupMembershipEvents(
        $input: PaginatedGroupMembershipEventsInput!
        $pagination: GroupMembershipEventPaginationInput
    ) {
        paginatedGroupMembershipEvents(input: $input, pagination: $pagination) {
            ...groupMembershipEventConnectionFields
        }
    }
`;

export const GET_GROUP_SESSION_ANALYTICS = gql`
    ${GROUP_SESSION_ANALYTICS_CONNECTION_FRAGMENT}
    query GroupSessionAnalytics($groupId: Int!, $pagination: GroupSessionPaginationInput) {
        groupSessionAnalytics(groupId: $groupId, pagination: $pagination) {
            ...groupSessionAnalyticsConnectionFields
        }
    }
`;

export const GET_PAGINATED_CLIENT_GROUP_MEMBERSHIPS = gql`
    ${CLIENT_GROUP_MEMBERSHIP_CONNECTION_FRAGMENT}
    query PaginatedClientGroupMemberships(
        $filters: PaginatedClientGroupMembershipsFiltersInput!
        $pagination: GroupMemberPaginationInput!
    ) {
        paginatedClientGroupMemberships(filters: $filters, pagination: $pagination) {
            ...clientGroupMembershipConnectionFields
        }
    }
`;

export const GET_PAGINATED_CLIENT_OWN_GROUP_MEMBERSHIPS = gql`
    ${CLIENT_OWN_GROUP_MEMBERSHIP_CONNECTION_FRAGMENT}
    query PaginatedClientOwnGroupMemberships(
        $filters: PaginatedClientGroupMembershipsFiltersInput!
        $pagination: GroupMemberPaginationInput!
    ) {
        paginatedClientGroupMemberships(filters: $filters, pagination: $pagination) {
            ...clientOwnGroupMembershipConnectionFields
        }
    }
`;

export const GET_GROUP_PROFIT = gql`
    ${GROUP_PROFIT_FRAGMENT}
    query GetGroupProfit($groupId: Int, $opts: AnalysisOptsInput) {
        groupSalesSummary(groupId: $groupId, opts: $opts) {
            ...groupProfitFields
        }
    }
`;

export const GET_GROUP_SALES_SUMMARY = gql`
    ${GROUP_SALES_SUMMARY_FRAGMENT}
    query GetGroupSalesSummary($groupId: Int, $opts: AnalysisOptsInput) {
        groupSalesSummary(groupId: $groupId, opts: $opts) {
            ...groupSalesSummaryFields
        }
    }
`;

export const GET_GROUP_SESSIONS_SUMMARY = gql`
    ${GROUP_SESSIONS_SUMMARY_FRAGMENT}

    query GetGroupSessionsSummary($groupId: Int, $opts: AnalysisOptsInput) {
        groupSessionsSummary(groupId: $groupId, opts: $opts) {
            ...groupSessionsSummaryFields
        }
    }
`;

export const GET_GROUP_OVERALL_SUMMARY = gql`
    ${GROUP_OVERALL_SUMMARY_FRAGMENT}

    query GetGroupOverAllSummary($opts: AnalysisOptsInput) {
        groupOverAllSummary(opts: $opts) {
            ...groupOverallSummaryFields
        }
    }
`;

export const GET_GROUP_SALES_TIME_SERIES = gql`
    ${GROUP_SALES_TIME_SERIES_FRAGMENT}
    query groupSalesTimeSeries($groupId: Int, $opts: AnalysisOptsInput) {
        groupSalesTimeSeries(groupId: $groupId, opts: $opts) {
            ...groupSalesTimeSeriesFields
        }
    }
`;

export const GET_GROUP_SESSIONS_TIME_SERIES = gql`
    ${GROUP_SESSIONS_TIME_SERIES_FRAGMENT}
    query groupSessionsTimeSeries($groupId: Int, $opts: AnalysisOptsInput) {
        groupSessionsTimeSeries(groupId: $groupId, opts: $opts) {
            ...groupSessionsTimeSeriesFields
        }
    }
`;

export const GET_CLIENT_GROUP_MEMBERSHIP = gql`
    ${CLIENT_GROUP_MEMBERSHIP_DETAILS_FRAGMENT}
    query ClientGroupMembership($groupId: Int!) {
        clientGroupMembership(groupId: $groupId) {
            ...clientGroupMembershipDetailsFields
        }
    }
`;

export const VALIDATE_GROUP_PROMO_CODE = gql`
    ${GROUP_PROMO_CODE_VALIDATION_FRAGMENT}
    query ValidateGroupPromoCode($input: ValidateGroupPromoCodeInput!) {
        validateGroupPromoCode(input: $input) {
            ...groupPromoCodeValidationFields
        }
    }
`;

/*
 * Uploads
 */

export const GET_UPLOAD_STATUS = gql`
    ${UPLOAD_FRAGMENT}
    query UploadStatus($uploadId: ID!) {
        uploadStatus(uploadId: $uploadId) {
            ...uploadFields
        }
    }
`;

export const GET_RECRUITING_HISTORY = gql`
    query RecruitingHistory($providerUserId: ID!) {
        recruitingHistory(providerUserId: $providerUserId) {
            auditLogId
            createdAt
            actor {
                userId
                name
            }
            from
            to
            source
            reason
            forced
        }
    }
`;

/*
 * Messaging
 */

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_MESSAGING_AVAILABILITY = gql`
    query GetMessagingAvailability {
        getMessagingAvailability {
            available
            hasExistingConversations
            hasAssignedProviderWithMessagingEnabled
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_MESSAGING_PROMPT_SUGGESTION_LIST = gql`
    query GetMessagingPromptSuggestionList {
        getMessagingPromptSuggestionList {
            key
            label
        }
    }
`;

export const SEARCH_MESSAGING_RECIPIENT_LIST = gql`
    ${MESSAGING_RECIPIENT_CONNECTION_FRAGMENT}
    query SearchMessagingRecipientList(
        $filters: MessagingRecipientFiltersInput
        $pagination: MessagingRecipientPaginationInput
    ) {
        searchMessagingRecipientList(filters: $filters, pagination: $pagination) {
            ...messagingRecipientConnectionFields
        }
    }
`;

export const GET_MESSAGING_CONVERSATION_LIST = gql`
    ${MESSAGING_CONVERSATION_CONNECTION_FRAGMENT}
    query GetMessagingConversationList(
        $filters: MessagingConversationFiltersInput
        $pagination: MessagingConversationPaginationInput
        $withConversationDetail: Boolean = false
    ) {
        getMessagingConversationList(filters: $filters, pagination: $pagination) {
            ...messagingConversationConnectionFields
        }
    }
`;

export const GET_MESSAGING_CONVERSATION = gql`
    ${MESSAGING_CONVERSATION_FRAGMENT}
    query GetMessagingConversation(
        $input: GetMessagingConversationInput!
        $withConversationDetail: Boolean = false
    ) {
        getMessagingConversation(input: $input) {
            ...messagingConversationFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_MESSAGING_CONVERSATION_LIST_PREVIEW = gql`
    ${MESSAGING_CONVERSATION_PREVIEW_CONNECTION_FRAGMENT}
    query GetMessagingConversationListPreview(
        $filters: MessagingConversationFiltersInput
        $pagination: MessagingConversationPaginationInput
    ) {
        getMessagingConversationList(filters: $filters, pagination: $pagination) {
            ...messagingConversationPreviewConnectionFields
        }
    }
`;

export const GET_CONVERSATION_MESSAGE_LIST = gql`
    ${MESSAGE_CONNECTION_FRAGMENT}
    query GetConversationMessageList(
        $filters: ConversationMessageFiltersInput!
        $pagination: ConversationMessagePaginationInput
    ) {
        getConversationMessageList(filters: $filters, pagination: $pagination) {
            ...messageConnectionFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_MESSAGING_UNREAD_COUNT = gql`
    query GetMessagingUnreadCount {
        getMessagingUnreadCount
    }
`;

export const GET_ESCALATION_LIST = gql`
    ${ESCALATION_CONNECTION_FRAGMENT}
    query GetEscalationList(
        $filters: EscalationFiltersInput
        $pagination: EscalationPaginationInput
    ) {
        getEscalationList(filters: $filters, pagination: $pagination) {
            ...escalationConnectionFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_ESCALATION = gql`
    ${ESCALATION_FRAGMENT}
    query GetEscalation($escalationId: Int, $conversationId: Int) {
        getEscalation(escalationId: $escalationId, conversationId: $conversationId) {
            ...escalationFields
        }
    }
`;

/** @deprecated Unused — no references found in the app (dead code; safe to remove). */
export const GET_ESCALATION_LIST_PREVIEW = gql`
    ${ESCALATION_PREVIEW_CONNECTION_FRAGMENT}
    query GetEscalationListPreview(
        $filters: EscalationFiltersInput
        $pagination: EscalationPaginationInput
    ) {
        getEscalationList(filters: $filters, pagination: $pagination) {
            ...escalationPreviewConnectionFields
        }
    }
`;

export const GET_REPORTED_MESSAGE_LIST = gql`
    ${REPORTED_MESSAGE_CONNECTION_FRAGMENT}
    query GetReportedMessageList(
        $filters: ReportedMessageFiltersInput
        $pagination: ReportedMessagePaginationInput
    ) {
        getReportedMessageList(filters: $filters, pagination: $pagination) {
            ...reportedMessageConnectionFields
        }
    }
`;

export const GET_CLIENT_MESSAGING_VISIBILITY = gql`
    query GetClientMessagingVisibility {
        getClientMessagingVisibility
    }
`;

/*
 * Care Circle Referral
 */

export const GET_REFERRAL_CODE = gql`
    ${REFERRAL_CODE_FRAGMENT}
    query GetReferralCode {
        getReferralCode {
            ...referralCodeFields
        }
    }
`;

export const GET_CARE_CIRCLE_STATUS = gql`
    query CareCircleStatus {
        careCircleStatus {
            enrolled
            joinedAt
        }
    }
`;

export const GET_CC_REFERRAL_PROGRESS = gql`
    ${CC_REFERRAL_PROGRESS_FRAGMENT}
    query GetCCReferralProgress {
        getCCReferralProgress {
            ...ccReferralProgressFields
        }
    }
`;

export const GET_REFERRAL_PROGRAM = gql`
    query ReferralProgram {
        referralProgram {
            maxLevels
            referralsPerReward
            rewardMonths
        }
    }
`;

export const GET_REACHLINK_PLUS_SUBSCRIPTION = gql`
    ${REACHLINK_PLUS_SUBSCRIPTION_FRAGMENT}
    query Subscription {
        subscription {
            ...reachlinkPlusSubscriptionFields
        }
    }
`;

export const GET_REACHLINK_PLUS_BILLING_HISTORY = gql`
    ${PAYMENT_FRAGMENT}

    query ReachLinkPlusBillingHistory {
        subscriptionBillingHistory {
            ...paymentFields
        }
    }
`;

export const GET_CALL_CONNECT_STATUS = gql`
    ${CALL_CONNECT_FRAGMENT}
    query CallConnectStatus($appointmentId: ID!) {
        callConnectStatus(appointmentId: $appointmentId) {
            ...callConnectFields
        }
    }
`;

export const GET_USAGE_SUMMARY = gql`
    query UsageSummary {
        usageSummary {
            usages {
                feature
                usage
                limit
                isUnlimited
                canUse
                shouldWarn
            }
        }
    }
`;

// Defaults to the requesting user when `providerId` is omitted; only an admin may
// ask about another provider. Provider side only — starting a call can still fail
// on the client's number.
export const IS_CALL_CONNECT_SUPPORTED_FOR_PROVIDER = gql`
    query IsCallConnectSupportedForProvider($providerId: ID) {
        isCallConnectSupportedForProvider(providerId: $providerId)
    }
`;

/*
 * In-app notifications
 */

/**
 * Fetches a page of in-app notifications, most recent first.
 *
 * NOTE: this query has a documented server-side write side effect — items of a
 * "read-on-view" type (currently appointment reminders) are marked read as a
 * consequence of being returned. Callers must therefore avoid cache-only reads.
 */
export const GET_IN_APP_NOTIFICATIONS = gql`
    query GetInAppNotifsList(
        $limit: Int
        $after: InAppNotificationCursorInput
        $before: InAppNotificationCursorInput
    ) {
        getInAppNotifsList(limit: $limit, after: $after, before: $before) {
            items {
                id
                type
                title
                subtitle
                body
                action
                data
                isRead
                createdAt
            }
            totalUnreadCount
            listUnreadCount
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor {
                    id
                    timestamp
                }
                endCursor {
                    id
                    timestamp
                }
            }
        }
    }
`;

/** Current count of unseen in-app notifications — drives the bell badge. */
export const GET_UNSEEN_NOTIFICATION_COUNT = gql`
    query GetUnseenNotificationCount {
        getUnseenNotificationCount
    }
`;

// The number Call Connect rings the requesting user from; null when their phone
// number is invalid or their country has no line provisioned.
export const GET_CALL_CONNECT_VOICE_NUMBER = gql`
    query GetCallConnectVoiceNumber {
        getCallConnectVoiceNumber
    }
`;

export const GET_PROVIDER_FEEDBACK_LOGS = gql`
    ${PROVIDER_FEEDBACK_LOG_LIST_ITEM_FRAGMENT}
    query GetProviderFeedbackLogs(
        $filter: ProviderFeedbackLogFilter
        $tab: FeedbackLogTab
        $sort: FeedbackLogSort
        $limit: Int
        $offset: Int
    ) {
        providerFeedbackLogs(
            filter: $filter
            tab: $tab
            sort: $sort
            limit: $limit
            offset: $offset
        ) {
            items {
                ...providerFeedbackLogListItemFields
            }
            totalCount
            tabCounts {
                open
                inProgress
                resolved
            }
        }
    }
`;

export const GET_PROVIDER_FEEDBACK_LOG = gql`
    ${PROVIDER_FEEDBACK_LOG_DETAIL_FRAGMENT}
    query GetProviderFeedbackLog($providerFeedbackLogId: Int!) {
        providerFeedbackLog(providerFeedbackLogId: $providerFeedbackLogId) {
            ...providerFeedbackLogDetailFields
        }
    }
`;

export const GET_PROVIDER_FEEDBACK_SESSION_OPTIONS = gql`
    query GetProviderFeedbackSessionOptions($providerUserId: Int!, $clientUserId: Int!) {
        providerFeedbackSessionOptions(providerUserId: $providerUserId, clientUserId: $clientUserId) {
            sessionRef {
                appointmentId
                groupSessionId
                groupId
            }
            dateTime
            status
        }
    }
`;

export const GET_PROVIDER_FEEDBACK_CLIENT_OPTIONS = gql`
    query GetProviderFeedbackClientOptions(
        $searchQuery: ClientSearchQuery!
        $offset: Int!
        $limit: Int!
    ) {
        clients(searchQuery: $searchQuery, offset: $offset, limit: $limit) {
            users {
                userId
                name
                photoUrl
            }
        }
    }
`;

export const GET_PROVIDER_FEEDBACK_GROUP_OPTIONS = gql`
    query GetProviderFeedbackGroupOptions(
        $filters: GroupSearchFiltersInput
        $pagination: GroupPaginationInput
    ) {
        paginatedGroups(filters: $filters, pagination: $pagination) {
            items {
                groupId
                name
                photoUrl
            }
        }
    }
`;

export const GET_PROVIDER_FEEDBACK_GROUP_SESSIONS = gql`
    query GetProviderFeedbackGroupSessions(
        $filters: GroupSessionSearchFiltersInput
        $pagination: GroupSessionPaginationInput
    ) {
        groupSessions(filters: $filters, pagination: $pagination) {
            items {
                groupSessionId
                groupId
                startDate
                status
                group {
                    groupId
                    name
                }
            }
        }
    }
`;

export const GET_POST_APPOINTMENT_ACTION = gql`
    query GetPostAppointmentAction {
        getPostAppointmentAction {
            action
            sessionRef {
                appointmentId
                groupSessionId
                groupId
            }
            provider {
                userId
                name
                avatar
            }
            dateTime
        }
    }
`;
